import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { Anchor } from "../../state/ui";
import { KEEP_OPEN, useOutsideClose } from "./Popover";

export interface MenuItem {
  key: string;
  label: ReactNode;
  shortcut?: string;
  icon?: ReactNode;
  active?: boolean;
  onSelect: () => void;
}

/** Dropdown list anchored below an element; scrolls the active item into view. */
export function Menu({ anchor, items, onClose, minWidth }: { anchor: Anchor; items: Array<MenuItem | "sep">; onClose: () => void; minWidth?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  useOutsideClose(ref, onClose);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    let top = anchor.y + anchor.height + 2;
    if (top + h > window.innerHeight - 8) top = Math.max(8, anchor.y - h - 2);
    setPos({ left: Math.min(Math.max(8, anchor.x), window.innerWidth - w - 8), top });
    el.querySelector(".menu-item.active")?.scrollIntoView({ block: "center" });
  }, [anchor.x, anchor.y, anchor.height]);

  return createPortal(
    <div ref={ref} className="menu" role="menu" {...KEEP_OPEN} style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999, minWidth: minWidth ?? Math.max(180, anchor.width) }}>
      {items.map((item, i) =>
        item === "sep" ? (
          <div key={`sep${i}`} className="menu-sep" />
        ) : (
          <button
            key={item.key}
            role="menuitem"
            className={`menu-item${item.active ? " active" : ""}`}
            onClick={() => {
              item.onSelect();
              onClose();
            }}
          >
            {item.icon}
            <span>{item.label}</span>
            {item.shortcut && <span className="shortcut">{item.shortcut}</span>}
          </button>
        )
      )}
    </div>,
    document.body
  );
}
